import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../../services/api';
import '../../v2-theme.css';

function CheckoutShiftPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get shift data from navigation state
  const shiftData = location.state?.shiftData || null;

  // If no shift data, redirect back to dashboard
  if (!shiftData) {
    navigate('/dashboard/restaurant');
    return null;
  }

  // Format date and time for display
  const formatDateTime = () => {
    const date = new Date(shiftData.shift_date);
    const dateStr = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
    const timeStr = `${shiftData.start_time} - ${shiftData.end_time}`;
    return `${dateStr} • ${timeStr}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Step 1: Create payment intent
      const { data: paymentData } = await api.post('/payments/create-shift-posting-intent', {
        shiftData
      });

      const { clientSecret, payment_intent_id } = paymentData;

      // Step 2: Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent.status === 'succeeded') {
        // Step 3: Post the shift with payment confirmation
        await api.post('/jobs/post-shift', {
          paymentIntentId: payment_intent_id,
          shiftData
        });

        // Redirect to success page
        navigate('/payment-success', {
          state: {
            type: 'shift_payment',
            amount: 19.00,
            message: 'Your shift has been posted successfully!'
          }
        });
      } else {
        throw new Error('Payment was not successful');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.error || err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        color: '#FFFFFF',
        fontFamily: '"Work Sans", sans-serif',
        fontSize: '16px',
        '::placeholder': {
          color: '#C0C0C0',
        },
      },
      invalid: {
        color: '#fa755a',
      },
    },
  };

  return (
    <div className="v2-page-wrapper">
      <div className="v2-container-narrow" style={{ maxWidth: '800px', padding: '40px 20px' }}>
        {/* Header */}
        <div className="v2-header-center" style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '20px' }}>
            <img src="/hotf-web-icon.png" alt="Hands On The Fly" style={{ width: '50px', height: '50px' }} />
            <h1 style={{ fontFamily: 'Times New Roman, serif', fontSize: '1.8rem', fontWeight: 'normal' }}>
              Hands On The Fly
            </h1>
          </div>
        </div>

        <h2 className="v2-heading-xl" style={{ textAlign: 'center', marginBottom: '10px' }}>
          Post Your Shift
        </h2>
        <p className="v2-text-muted" style={{ textAlign: 'center', marginBottom: '40px' }}>
          Complete payment to publish your shift posting
        </p>

        {/* Shift Summary */}
        <div className="v2-card" style={{ marginBottom: '30px', padding: '25px' }}>
          <div className="v2-heading-md" style={{ marginBottom: '20px' }}>
            Shift Details
          </div>

          <div style={{ borderBottom: '1px solid var(--dark-steel)', padding: '12px 0', display: 'flex', justifyContent: 'space-between' }}>
            <span className="v2-text-muted">Position</span>
            <span style={{ fontWeight: '500' }}>{shiftData.position}</span>
          </div>

          <div style={{ borderBottom: '1px solid var(--dark-steel)', padding: '12px 0', display: 'flex', justifyContent: 'space-between' }}>
            <span className="v2-text-muted">Date & Time</span>
            <span style={{ fontWeight: '500' }}>{formatDateTime()}</span>
          </div>

          <div style={{ borderBottom: '1px solid var(--dark-steel)', padding: '12px 0', display: 'flex', justifyContent: 'space-between' }}>
            <span className="v2-text-muted">Hourly Pay</span>
            <span style={{ fontWeight: '500' }}>${shiftData.hourly_rate}/hr</span>
          </div>

          <div style={{ borderBottom: '1px solid var(--dark-steel)', padding: '12px 0', display: 'flex', justifyContent: 'space-between' }}>
            <span className="v2-text-muted">Posting Fee</span>
            <span style={{ fontWeight: '500' }}>$19.00</span>
          </div>

          <div style={{ paddingTop: '20px', marginTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '1.2rem', fontWeight: '600' }}>
            <span className="v2-text-muted">Total</span>
            <span>$19.00</span>
          </div>
        </div>

        {/* Info Box */}
        <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderLeft: '3px solid var(--steel)', padding: '15px', marginBottom: '20px', fontSize: '0.9rem', color: 'var(--silver)' }}>
          <strong>Pay Per Post Plan:</strong> You only pay when you post a shift. No monthly fees or commitments.<br />
          <strong>Tip:</strong> Post 6+ shifts/month? Upgrade to Unlimited ($99/mo) and save money!
        </div>

        {/* Payment Card */}
        <div className="v2-card" style={{ padding: '30px', marginBottom: '20px' }}>
          <form onSubmit={handleSubmit}>
            <div className="v2-heading-md" style={{ marginBottom: '20px' }}>
              Payment Information
            </div>

            <div className="v2-form-group">
              <label className="v2-label">Card Details</label>
              <div style={{
                padding: '12px',
                background: 'var(--charcoal)',
                border: '1px solid var(--dark-steel)',
                borderRadius: '2px'
              }}>
                <CardElement options={cardElementOptions} />
              </div>
              {error && (
                <div style={{ color: '#fa755a', fontSize: '0.85rem', marginTop: '8px' }}>
                  {error}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="v2-btn v2-btn-primary"
              disabled={!stripe || loading}
              style={{ width: '100%', marginTop: '10px' }}
            >
              {loading ? 'Processing...' : 'Pay $19.00 & Post Shift'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '20px', color: 'var(--dark-steel)', fontSize: '0.85rem' }}>
            🔒 Secure payment powered by Stripe<br />
            Your card information is encrypted and never stored on our servers.
          </div>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              onClick={() => navigate('/dashboard/restaurant')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--silver)',
                cursor: 'pointer',
                fontSize: '0.9rem',
                transition: 'color 0.3s ease'
              }}
            >
              ← Cancel and return to dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(26, 26, 26, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="v2-spinner"></div>
        </div>
      )}
    </div>
  );
}

export default CheckoutShiftPayment;
